import { useState, useEffect } from 'react'
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Briefcase,
  Building,
  Calendar,
  GraduationCap,
  Languages,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  Download,
  User,
  FileText,
  Send,
  Check,
  Lock,
  Clock,
} from 'lucide-react'
import recruiterService, {
  FullCandidate,
  ContactInfo,
  QuotaBalance,
} from '@/services/recruiterService'
import recruiterCandidateService from '@/services/recruiterCandidateService'
import Button from '@/components/common/Button'
import { useAuth } from '@/stores/authStore'
import { toast } from '@/stores/toastStore'

interface LocationState {
  candidate?: FullCandidate
  contactInfo?: ContactInfo | null
}

export default function CandidateDetailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const {} = useParams<{ candidateId: string }>()
  const { t } = useTranslation('recruiter')
  const { refreshUser } = useAuth((state) => ({
    refreshUser: state.refreshUser,
  }))

  const state = (location.state as LocationState) || {}
  const [candidate, setCandidate] = useState<FullCandidate | null>(state.candidate || null)
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(state.contactInfo || null)
  const [isRevealingContact, setIsRevealingContact] = useState(false)
  const [checkingRevealStatus, setCheckingRevealStatus] = useState(false)
  const [isRevealed, setIsRevealed] = useState(false)
  const [quota, setQuota] = useState<QuotaBalance | null>(null)

  // Fetch quota on mount
  useEffect(() => {
    recruiterService
      .getQuota()
      .then(setQuota)
      .catch(() => {})
  }, [])

  // Check reveal status
  useEffect(() => {
    if (!candidate) return
    setCheckingRevealStatus(true)
    recruiterCandidateService
      .checkRevealStatus(candidate.id)
      .then((status) => {
        setIsRevealed(status.isRevealed)
        if (status.isRevealed && !contactInfo) {
          handleRevealContact(candidate.id, true)
        }
      })
      .catch(() => {
        setIsRevealed(candidate.contactRevealed || false)
      })
      .finally(() => setCheckingRevealStatus(false))
  }, [candidate?.id])

  if (!candidate) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-gray-600">Candidate not found.</p>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    )
  }

  const isIdentityRevealed = !!(candidate.firstName || candidate.lastName)
  const displayName = isIdentityRevealed
    ? `${candidate.firstName ?? ''} ${candidate.lastName ?? ''}`.trim()
    : 'Anonymous Candidate'

  const handleRevealContact = async (cId: string, alreadyRevealed = false) => {
    setIsRevealingContact(true)
    try {
      const {
        message,
        contact,
        alreadyRevealed: wasAlreadyRevealed,
      } = await recruiterService.revealContact(cId)
      setContactInfo(contact)
      setCandidate((prev) => (prev ? { ...prev, contactRevealed: true } : null))
      if (!wasAlreadyRevealed && !alreadyRevealed) {
        toast.success(message || t('profileReveal.search.contactRevealed'))
        refreshUser()
        const updated = await recruiterService.viewCandidate(cId)
        setCandidate(updated)
        recruiterService
          .getQuota()
          .then(setQuota)
          .catch(() => {})
      }
      setIsRevealed(true)
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('profileReveal.search.quotaExhausted'))
    } finally {
      setIsRevealingContact(false)
    }
  }

  const quotaLabel = quota ? `${quota.contactRevealsUsed} / ${quota.contactRevealsAllowed}` : '…'

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Back button */}
      <Button
        variant="secondary"
        onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </Button>

      {/* Hero Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl px-8 py-7 mb-6 relative">
        <div className="flex items-start gap-6 pr-4">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {candidate.profilePhotoUrl ? (
              <img
                src={candidate.profilePhotoUrl}
                alt={displayName}
                className="w-24 h-24 rounded-xl rounded-avatar object-cover shadow-lg border-4 border-white/20"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl rounded-avatar bg-white/20 flex items-center justify-center border-4 border-white/20">
                <User className="w-12 h-12 text-white/80" />
              </div>
            )}
          </div>

          {/* Identity & meta */}
          <div className="flex-grow min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-white truncate">{displayName}</h1>
              {!isIdentityRevealed && (
                <span className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white/20 text-white/80">
                  <Lock className="w-3 h-3" /> Reveal to unlock
                </span>
              )}
            </div>
            {candidate.jobTitle && (
              <p className="text-lg text-white/90 font-medium mb-2">{candidate.jobTitle}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-white/80 text-sm mb-4">
              {candidate.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {candidate.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {candidate.yearsExperience} yrs experience
              </span>
              {candidate.employmentStatus && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                  {candidate.employmentStatus.replace(/_/g, ' ')}
                </span>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              {isIdentityRevealed && (
                <Link
                  to={`/recruiter/job-offers/new?candidateId=${candidate.id}&candidateName=${encodeURIComponent(displayName)}`}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900"
                  >
                    <Send className="w-4 h-4" />
                    {t('profileReveal.modal.candidate.sendOffer')}
                  </Button>
                </Link>
              )}
              {candidate.cvFileUrl && (
                <a href={candidate.cvFileUrl} target="_blank" rel="noreferrer">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                    <Download className="w-4 h-4" />
                    {t('profileReveal.modal.candidate.downloadCV')}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: main timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* About */}
          <section className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('profileReveal.modal.sections.about')}
              </h2>
            </div>
            {candidate.bio ? (
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{candidate.bio}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">No bio provided.</p>
            )}
          </section>

          {/* Work Experience */}
          <section className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('profileReveal.modal.sections.workExperience')}
              </h2>
            </div>
            {candidate.workExperiences && candidate.workExperiences.length > 0 ? (
              <div className="space-y-4">
                {candidate.workExperiences.map((exp) => (
                  <div key={exp.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <Building className="w-5 h-5 text-gray-500" />
                      </div>
                      <div className="flex-grow min-w-0">
                        <h4 className="font-bold text-gray-900">{exp.jobTitle}</h4>
                        <p className="text-gray-700 font-medium text-sm">{exp.companyName}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(exp.startDate).getFullYear()} –{' '}
                          {exp.isCurrent
                            ? t('profileReveal.modal.candidate.present')
                            : exp.endDate
                              ? new Date(exp.endDate).getFullYear()
                              : '?'}
                        </p>
                        {exp.description && (
                          <p className="text-gray-600 mt-2 text-sm leading-relaxed">
                            {exp.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No work experience listed.</p>
            )}
          </section>

          {/* Education */}
          <section className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('profileReveal.modal.sections.education')}
              </h2>
            </div>
            {candidate.educations && candidate.educations.length > 0 ? (
              <div className="space-y-3">
                {candidate.educations.map((edu) => (
                  <div key={edu.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-5 h-5 text-gray-500" />
                      </div>
                      <div className="flex-grow min-w-0">
                        <h4 className="font-bold text-gray-900">{edu.degree}</h4>
                        <p className="text-gray-700 font-medium text-sm">{edu.institution}</p>
                        {edu.fieldOfStudy && (
                          <p className="text-xs text-gray-500 mt-0.5">{edu.fieldOfStudy}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No education listed.</p>
            )}
          </section>
        </div>

        {/* Right: sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <section className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex items-center gap-3 p-5 border-b border-gray-100">
              <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('profileReveal.modal.sections.contact')}
              </h2>
            </div>

            {checkingRevealStatus ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                <p className="text-gray-500 mt-3 text-sm">
                  {t('profileReveal.modal.contact.loading')}
                </p>
              </div>
            ) : isRevealed && contactInfo ? (
              <div className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-700 font-medium text-sm">
                    {t('profileReveal.modal.contact.revealed')}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">
                        {t('profileReveal.modal.contact.email')}
                      </p>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="text-blue-600 hover:underline font-medium text-sm truncate block"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                  {contactInfo.phone && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 mb-0.5">
                          {t('profileReveal.modal.contact.phone')}
                        </p>
                        <a
                          href={`tel:${contactInfo.phone}`}
                          className="text-gray-900 font-medium text-sm"
                        >
                          {contactInfo.phone}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-5">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <Lock className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('profileReveal.modal.contact.description')}
                  </p>
                </div>
                <Button
                  onClick={() => handleRevealContact(candidate.id)}
                  isLoading={isRevealingContact}
                  disabled={quota ? quota.contactRevealsRemaining <= 0 : false}
                  variant="primary"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>{t('profileReveal.modal.contact.revealButton')}</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded text-xs font-mono">
                    {quotaLabel}
                  </span>
                </Button>
                {quota && quota.contactRevealsRemaining <= 0 && (
                  <p className="text-xs text-red-500 text-center mt-2">No reveals remaining.</p>
                )}
                {quota && quota.contactRevealsRemaining > 0 && (
                  <p className="text-xs text-gray-500 text-center mt-2">
                    {quota.contactRevealsRemaining} reveal
                    {quota.contactRevealsRemaining !== 1 ? 's' : ''} remaining
                  </p>
                )}
              </div>
            )}
          </section>

          {/* Skills */}
          <section className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('profileReveal.modal.sections.skills')}
              </h2>
            </div>
            {candidate.skills && candidate.skills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {candidate.skills.map((skill) => (
                  <span
                    key={skill.skillId}
                    className="px-3 py-1 rounded-lg text-sm font-medium bg-indigo-50 text-indigo-800 border border-indigo-100"
                  >
                    {skill.skillName}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No skills listed.</p>
            )}
          </section>

          {/* Languages */}
          <section className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
                <Languages className="w-5 h-5 text-teal-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                {t('profileReveal.modal.sections.languages')}
              </h2>
            </div>
            {candidate.languages && candidate.languages.length > 0 ? (
              <div className="space-y-2">
                {candidate.languages.map((lang) => (
                  <div key={lang.languageId} className="flex justify-between items-center">
                    <span className="text-gray-900 font-medium text-sm">{lang.languageName}</span>
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                      {lang.proficiency}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No languages listed.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
